import { buildUrl, useApi } from "../api";
import type { SiteInfo } from "../api";
import { BetreiberHinweis, Card, ErrorNote, Loading } from "../components";

/**
 * Datenschutzerklärung nach Artikel 13 DSGVO.
 *
 * Beschreibt nur, was die Anwendung tatsächlich tut: Der Webserver protokolliert
 * Zugriffe, die Oberfläche merkt sich die gewählte Darstellung im Browser. Kein
 * Tracking, keine Einbindung fremder Dienste, keine Weitergabe.
 */
export default function Datenschutz() {
  const { data, loading, error, reload } = useApi<SiteInfo>(buildUrl("/api/site-info"));

  if (error) return <ErrorNote message={error} onRetry={reload} />;
  if (loading || !data) return <Loading rows={6} />;

  const kontakt = data.email ? <a href={`mailto:${data.email}`}>{data.email}</a> : "—";

  return (
    <>
      <div className="page-head">
        <h1>Datenschutz</h1>
        <p>
          Informationen zur Verarbeitung personenbezogener Daten nach Artikel 13
          Datenschutz-Grundverordnung.
        </p>
        <BetreiberHinweis betreiber={data.betreiber} hauptseite={data.hauptseite} />
      </div>

      <Card title="Kurz gefasst">
        <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: "0.95rem" }}>
          <li>Kein Tracking, keine Analysewerkzeuge, keine Werbung.</li>
          <li>
            Keine eingebundenen Dienste Dritter — keine Schriftarten, Karten oder
            Skripte von fremden Servern.
          </li>
          <li>Keine Konten, keine Anmeldung, keine Formulare.</li>
          <li>
            Der Webserver protokolliert Zugriffe für {data.logs_tage} Tage, um den
            Betrieb sicherzustellen.
          </li>
          <li>
            Im Browser wird ausschließlich die gewählte Darstellung (hell oder
            dunkel) gespeichert.
          </li>
        </ul>
      </Card>

      <Card title="Verantwortlicher">
        <p style={{ marginTop: 0, marginBottom: 0 }}>
          {data.betreiber || "—"}
          {data.vertretung ? (
            <>
              <br />
              vertreten durch {data.vertretung}
            </>
          ) : null}
          {data.strasse ? (
            <>
              <br />
              {data.strasse}
              <br />
              {data.plz_ort}
              <br />
              {data.land}
            </>
          ) : null}
          <br />
          {kontakt}
        </p>
      </Card>

      <Card title="Zugriffsprotokolle des Webservers">
        <p style={{ marginTop: 0 }}>
          Bei jedem Aufruf werden technisch bedingt Daten übertragen, die der
          Webserver protokolliert:
        </p>
        <ul style={{ paddingLeft: "1.2rem", fontSize: "0.95rem" }}>
          <li>IP-Adresse des anfragenden Geräts</li>
          <li>Datum und Uhrzeit des Zugriffs</li>
          <li>aufgerufene Adresse und übertragene Datenmenge</li>
          <li>Statusmeldung, verweisende Seite und Browserkennung</li>
        </ul>
        <dl className="meta">
          <div>
            <dt>Zweck</dt>
            <dd style={{ fontSize: "0.9rem" }}>
              Betrieb und Sicherheit des Angebots, Erkennung und Abwehr von
              Störungen und Angriffen
            </dd>
          </div>
          <div>
            <dt>Rechtsgrundlage</dt>
            <dd style={{ fontSize: "0.9rem" }}>
              Artikel 6 Absatz 1 Buchstabe f DSGVO — berechtigtes Interesse am
              störungsfreien und sicheren Betrieb
            </dd>
          </div>
          <div>
            <dt>Speicherdauer</dt>
            <dd style={{ fontSize: "0.9rem" }}>
              {data.logs_tage} Tage, danach automatische Löschung
            </dd>
          </div>
        </dl>
        <p style={{ fontSize: "0.9rem", color: "var(--ink-2)", marginBottom: 0 }}>
          Die Protokolle werden nicht mit anderen Daten zusammengeführt und dienen
          nicht dazu, einzelne Personen zu identifizieren.
        </p>
      </Card>

      <Card title="Speicherung im Browser">
        <p style={{ marginTop: 0 }}>
          Diese Seite setzt <strong>keine Cookies</strong>. Gespeichert wird
          ausschließlich die von Ihnen gewählte Darstellung — hell oder dunkel — im
          lokalen Speicher Ihres Browsers unter dem Namen{" "}
          <code>politwatch-theme</code>.
        </p>
        <p style={{ fontSize: "0.9rem", color: "var(--ink-2)" }}>
          Dieser Eintrag entsteht nur, wenn Sie die Darstellung aktiv umschalten. Er
          enthält lediglich den Wert „light" oder „dark", verlässt Ihr Gerät nicht
          und lässt keinen Rückschluss auf Ihre Person zu. Als Speicherung einer von
          Ihnen ausdrücklich gewünschten Einstellung ist er nach § 25 Absatz 2 Nummer
          2 Telekommunikation-Digitale-Dienste-Datenschutz-Gesetz einwilligungsfrei —
          deshalb gibt es hier kein Zustimmungsfenster.
        </p>
        <p style={{ fontSize: "0.9rem", marginBottom: 0 }}>
          Löschen können Sie den Eintrag jederzeit über die Einstellungen Ihres
          Browsers oder indem Sie die Darstellung auf „System" zurückstellen.
        </p>
      </Card>

      <Card title="Daten über Abgeordnete und Fraktionen">
        <p style={{ marginTop: 0 }}>
          Dieses Angebot verarbeitet Daten über das Abstimmungsverhalten von
          Mitgliedern des Deutschen Bundestages. Diese Daten stammen aus
          namentlichen Abstimmungen, die nach der Geschäftsordnung des Bundestages
          öffentlich sind, und werden von abgeordnetenwatch.de unter der Lizenz
          CC0 bereitgestellt.
        </p>
        <p style={{ fontSize: "0.9rem", color: "var(--ink-2)", marginBottom: 0 }}>
          In der Oberfläche werden die Stimmen zu Fraktionen zusammengefasst
          dargestellt; einzelne Abgeordnete werden nicht namentlich ausgewertet.
          Namen können in den übernommenen Beschreibungstexten der Quelle
          vorkommen, wenn dort ein von der Fraktionslinie abweichendes
          Stimmverhalten geschildert wird. Rechtsgrundlage ist Artikel 6 Absatz 1
          Buchstabe f DSGVO — das berechtigte Interesse an der Nachvollziehbarkeit
          parlamentarischen Handelns, das bei Mandatsträgern in Ausübung ihres
          Amtes das Interesse an Nichtveröffentlichung überwiegt.
        </p>
      </Card>

      <Card title="Ihre Rechte">
        <p style={{ marginTop: 0 }}>
          Sie haben das Recht auf Auskunft über die zu Ihnen gespeicherten Daten
          (Artikel 15), auf Berichtigung (Artikel 16), auf Löschung (Artikel 17),
          auf Einschränkung der Verarbeitung (Artikel 18) sowie auf
          Datenübertragbarkeit (Artikel 20 DSGVO).
        </p>
        <p>
          Gegen die Verarbeitung auf Grundlage berechtigter Interessen können Sie
          jederzeit Widerspruch einlegen (Artikel 21 DSGVO). Wenden Sie sich dazu an{" "}
          {kontakt}.
        </p>
        <p style={{ marginBottom: 0 }}>
          Unabhängig davon können Sie sich bei einer Datenschutz-Aufsichtsbehörde
          beschweren (Artikel 77 DSGVO). Zuständig ist die Behörde Ihres
          Wohnsitzlandes; eine Übersicht führt der{" "}
          <a
            href="https://www.bfdi.bund.de/DE/Service/Anschriften/anschriften_node.html"
            target="_blank"
            rel="noreferrer noopener"
          >
            Bundesbeauftragte für den Datenschutz
          </a>
          .
        </p>
      </Card>

      <Card title="Hosting">
        <p style={{ marginTop: 0, marginBottom: 0 }}>
          Das Angebot wird auf einem angemieteten Server betrieben. Der Anbieter
          verarbeitet die oben genannten Zugriffsdaten weisungsgebunden im Rahmen
          einer Vereinbarung zur Auftragsverarbeitung nach Artikel 28 DSGVO.
        </p>
      </Card>
    </>
  );
}
